import { displayDateTimeToMinute, Entity, FieldType, PersistenceType } from 'gossamer-unified';
import { Event } from './Event';
import { UserView } from './UserView';
import { Institution } from './Institution';

export interface PaymentSummaryViewRow {
  event_id: number;
  institution_id: number;
  user_id: string;
  event_end_at: string;
  payout_due_at: string;
  paid_pending_bookings: number;
  paid_pending_amount: number;
  paid_out_bookings: number;
  paid_out_amount: number;
  refunded_net_bookings: number;
  refunded_net_amount: number;
  not_refunded_bookings: number;
  not_refunded_amount: number;
  refunded_gross_bookings: number;
  refunded_gross_amount: number;
}

export const PaymentSummaryView: Entity<PaymentSummaryViewRow> = {
  id: 'PaymentSummaryView',
  title: 'Payments',
  service: 'main',
  rowLabel: (row: PaymentSummaryViewRow) => String(row.event_id),
  primaryKeyFields: ['event_id'],
  source: {
    tableName: 'public.payment_summary_view',
  },
  security: [
    { role: 'user', action: 'read', access: 'conditional', condition: "user_id = '{userId}'" },
    {
      role: 'admin',
      action: 'read',
      access: 'conditional',
      condition: "user_id = '{userId}' OR institution_id = '{adminInstitutionId}'",
    },
    { role: 'staff', action: 'read', access: 'all' },
  ],
  defaultSort: [['event_end_at', 'DESC']],
  defaultFilters: [
    { fieldId: 'event_id', operator: 'eq', value: '' },
    { fieldId: 'user_id', operator: 'eq', value: '' },
    { fieldId: 'institution_id', operator: 'eq', value: '' },
  ],
  fields: {
    event_id: {
      seq: 10,
      type: FieldType.Reference,
      label: 'Event',
      refEntity: Event,
      mandatory: true,
      listColumn: true,
    },
    user_id: {
      seq: 20,
      type: FieldType.Reference,
      label: 'User',
      refEntity: UserView,
      persistence: PersistenceType.BackEndManaged,
      visible: false,
    },
    institution_id: {
      seq: 25,
      type: FieldType.Reference,
      label: 'Institution',
      refEntity: Institution,
      persistence: PersistenceType.BackEndManaged,
      visible: false,
    },
    event_end_at: {
      seq: 30,
      type: FieldType.Datetime,
      label: 'Event End',
      persistence: PersistenceType.BackEndManaged,
      listColumn: true,
      getDisplayText: (value: string) => displayDateTimeToMinute(value, '-'),
    },
    payout_due_at: {
      seq: 35,
      type: FieldType.Datetime,
      label: 'Payout Due',
      persistence: PersistenceType.BackEndGenerated,
      listColumn: true,
      getDisplayText: (value: string) => displayDateTimeToMinute(value, '-'),
    },
    paid_pending_bookings: {
      seq: 40,
      type: FieldType.Integer,
      label: 'Bookings Awaiting Payment',
      persistence: PersistenceType.BackEndManaged,
      listColumn: true,
    },
    paid_pending_amount: {
      seq: 50,
      type: FieldType.Decimal,
      label: "You'll Receive",
      decimalDigits: 2,
      staticPrefix: '£',
      persistence: PersistenceType.BackEndManaged,
      listColumn: true,
    },
    paid_out_bookings: {
      seq: 60,
      type: FieldType.Integer,
      label: 'Bookings Paid',
      persistence: PersistenceType.BackEndManaged,
      listColumn: true,
    },
    paid_out_amount: {
      seq: 70,
      type: FieldType.Decimal,
      label: 'Amount Paid',
      decimalDigits: 2,
      staticPrefix: '£',
      persistence: PersistenceType.BackEndManaged,
      listColumn: true,
    },
    refunded_net_bookings: {
      seq: 80,
      type: FieldType.Integer,
      label: 'Refunded Booking Cancellations',
      persistence: PersistenceType.BackEndManaged,
      listColumn: true,
    },
    refunded_net_amount: {
      seq: 90,
      type: FieldType.Decimal,
      label: 'Refunded Booking Cancellations',
      decimalDigits: 2,
      staticPrefix: '£',
      persistence: PersistenceType.BackEndManaged,
      listColumn: true,
    },
    not_refunded_bookings: {
      seq: 100,
      type: FieldType.Integer,
      label: 'Not Refunded Booking Cancellations',
      persistence: PersistenceType.BackEndManaged,
      listColumn: true,
    },
    not_refunded_amount: {
      seq: 110,
      type: FieldType.Decimal,
      label: 'Not Refunded Booking Cancellations',
      decimalDigits: 2,
      staticPrefix: '£',
      persistence: PersistenceType.BackEndManaged,
      listColumn: true,
    },
    refunded_gross_bookings: {
      seq: 120,
      type: FieldType.Integer,
      label: 'Event Cancelled Bookings',
      persistence: PersistenceType.BackEndManaged,
      listColumn: true,
    },
    refunded_gross_amount: {
      seq: 130,
      type: FieldType.Decimal,
      label: 'Event Cancelled Amount',
      decimalDigits: 2,
      staticPrefix: '£',
      persistence: PersistenceType.BackEndManaged,
      listColumn: true,
    },
  },
};
